import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import FluidButton from 'ui/FluidButton';
import Checkbox from 'ui/Checkbox';
import { groupBy } from 'lodash-es';
import { IBootstrapCountry } from 'services/BackendApi/types/country';
import SingleDateInput from 'pureUi/SingleDateInput';
import { formatDate } from 'utils';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';

interface IAvailabilityBookingSectionProps {
  countryCodes: string[];
  bookingStart: string;
  bookingEnd: string;
  stayPeriodStart: string;
  stayPeriodEnd: string;
  updateCountryCodes: (countryCodes: string[]) => void;
  resetCountryCodes: () => void;
  updateBookingStart: (date: string) => void;
  updateBookingEnd: (date: string) => void;
  updateStayPeriodStart: (date: string) => void;
  updateStayPeriodEnd: (date: string) => void;
}

export const AvailabilityBookingSection = ({
  countryCodes,
  bookingStart,
  bookingEnd,
  stayPeriodStart,
  stayPeriodEnd,
  updateCountryCodes,
  resetCountryCodes,
  updateBookingStart,
  updateBookingEnd,
  updateStayPeriodStart,
  updateStayPeriodEnd,
}: IAvailabilityBookingSectionProps) => {
  const [openRegions, setOpenRegions] = useState<string[]>([]);

  const countries = useSelector(getBootstrapCountriesSelector);
  const countriesByRegion = useMemo(() => groupBy(countries, 'region'), [countries]);

  const handleRegionToggle = (region: string, isOpen: boolean) => {
    setOpenRegions(prev => (isOpen ? [...prev, region] : prev.filter(r => r !== region)));
  };

  const handleCountryToggle = (countryCode: string, checked: boolean) => {
    if (checked) {
      updateCountryCodes([...countryCodes, countryCode]);
    } else {
      updateCountryCodes(countryCodes.filter(code => code !== countryCode));
    }
  };

  const getSelectedCountsForRegion = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedInRegion = regionCountries.filter(country => countryCodes.includes(country.code)).length;
    return selectedInRegion;
  };

  const getRegionSummaryText = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedCount = getSelectedCountsForRegion(region);

    // If no countries selected globally, show "All countries"
    if (countryCodes.length === 0) {
      return 'All countries';
    }

    // If all countries in this region are selected, show "All countries"
    if (selectedCount === regionCountries.length && regionCountries.length > 0) {
      return 'All countries';
    }

    // Otherwise show the count
    return `${selectedCount} countries`;
  };

  const handleRegionCheckboxToggle = (region: string, checked: boolean) => {
    const regionCountries = countriesByRegion[region] || [];
    const regionCodes = regionCountries.map(country => country.code);

    if (checked) {
      // Add all countries from this region
      const newCountryCodes = [...new Set([...countryCodes, ...regionCodes])];
      updateCountryCodes(newCountryCodes);
    } else {
      // Remove all countries from this region
      const newCountryCodes = countryCodes.filter(code => !regionCodes.includes(code));
      updateCountryCodes(newCountryCodes);
    }
  };

  const isRegionFullySelected = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    if (regionCountries.length === 0) return false;
    return regionCountries.every(country => countryCodes.includes(country.code));
  };

  const isRegionPartiallySelected = (region: string) => {
    const regionCountries = countriesByRegion[region] || [];
    const selectedCount = getSelectedCountsForRegion(region);
    return selectedCount > 0 && selectedCount < regionCountries.length;
  };

  return (
    <div className="space-y-[10px]">
      {/* Section Title */}
      <span className="font-bold text-[17px] text-gray-800 font-pt-sans">Availability & Booking</span>

      {/* Country Selection */}
      <div className="space-y-2">
        {/* Header Row */}
        <div className="flex justify-between items-center">
          <span className="text-[15px] font-pt-sans text-gray-700">Show to TA from these Countries:</span>
          <div className="flex items-center gap-2.5">
            <span className="text-[15px] font-pt-sans text-brown-prime">
              {countryCodes.length === 0
                ? 'All countries selected by default'
                : countryCodes.length === 1
                ? '1 country selected'
                : `${countryCodes.length} countries selected`}
            </span>
            <FluidButton
              type="secondary"
              onClick={resetCountryCodes}
              className="bg-white border border-brown-prime text-brown-prime hover:bg-brown-prime hover:text-white"
            >
              Reset Selection
            </FluidButton>
          </div>
        </div>

        {/* Region Accordions */}
        <div className="space-y-0">
          {Object.entries(countriesByRegion).map(([region, regionCountries], index) => {
            const selectedCount = getSelectedCountsForRegion(region);
            const isOpen = openRegions.includes(region);
            const isEven = index % 2 === 0;

            return (
              <AccordionStackable
                key={region}
                isOpen={isOpen}
                onToggle={newIsOpen => handleRegionToggle(region, newIsOpen)}
                buttonClassName={`${isOpen ? '!bg-green-20' : isEven ? '!bg-ivory' : '!bg-white'}`}
                wrapperClassName="[&_.accordion-stackable-chevron_i]:text-brown-prime"
                summary={
                  <div className="flex justify-between items-center w-full mr-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isRegionFullySelected(region)}
                        indeterminate={isRegionPartiallySelected(region)}
                        onChange={e => handleRegionCheckboxToggle(region, (e.target as HTMLInputElement).checked)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="text-[15px] font-pt-sans text-gray-700">{region}</span>
                    </div>
                    <span className="text-[13px] font-pt-sans text-gray-600">{getRegionSummaryText(region)}</span>
                  </div>
                }
              >
                <div className="p-5 grid grid-cols-5 gap-4">
                  {regionCountries.map(country => (
                    <label key={country.code} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={countryCodes.includes(country.code)}
                        onChange={e => handleCountryToggle(country.code, (e.target as HTMLInputElement).checked)}
                      />
                      <span className="text-[14px] font-pt-sans text-gray-700">{country.name}</span>
                    </label>
                  ))}
                </div>
              </AccordionStackable>
            );
          })}
        </div>
      </div>

      {/* Date Inputs */}
      <div className="w-fit grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-[40px]">
        {/* Booking Start */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Booking Start</label>
          <SingleDateInput
            value={bookingStart && bookingStart !== '' ? new Date(bookingStart) : null}
            onChange={date => updateBookingStart(formatDate(date))}
            placeholder=""
            enablePastDates={true}
            className="w-full"
            buttonClassName="!bg-white-hard"
          />
        </div>

        {/* Booking End */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Booking End</label>
          <SingleDateInput
            value={bookingEnd && bookingEnd !== '' ? new Date(bookingEnd) : null}
            onChange={date => updateBookingEnd(formatDate(date))}
            placeholder=""
            enablePastDates={true}
            className="w-full"
            buttonClassName="!bg-white-hard"
          />
        </div>

        {/* Stay Period Start */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Stay Period Start</label>
          <SingleDateInput
            value={stayPeriodStart && stayPeriodStart !== '' ? new Date(stayPeriodStart) : null}
            onChange={date => updateStayPeriodStart(formatDate(date))}
            placeholder=""
            enablePastDates={true}
            className="w-full"
            buttonClassName="!bg-white-hard"
          />
        </div>

        {/* Stay Period End */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Stay Period End</label>
          <SingleDateInput
            value={stayPeriodEnd && stayPeriodEnd !== '' ? new Date(stayPeriodEnd) : null}
            onChange={date => updateStayPeriodEnd(formatDate(date))}
            placeholder=""
            enablePastDates={true}
            className="w-full"
            buttonClassName="!bg-white-hard"
          />
        </div>
      </div>
    </div>
  );
};
